import { getClientEnv, isSupabaseConfigured } from "@/lib/env";
import { supabase } from "@/lib/supabase";
import {
  getRuntimePricingCatalogSnapshot,
  getRuntimeServicePricing,
} from "@/features/pricing/runtimePricing";
import type {
  StoryImportBulkAcceptElementType,
  StoryImportCreditQuote,
  StoryImportDraft,
  StoryImportErrorCode,
  StoryImportReviewActionResult,
  StoryImportReviewBundle,
  StoryImportSummary,
} from "@/features/story-import/story-import.types";
import { calculateStoryImportCreditCost } from "@/features/story-import/storyImportPricing";
import {
  buildStoryImportStoragePath,
  mapImportJobRecord,
  mapStoryImportReviewBundle,
  mapStoryImportSummaryArtifact,
} from "@/features/story-import/storyImportMappers";
import type { SubscriptionTier } from "@/types/projects";

export class StoryImportClientError extends Error {
  constructor(
    public readonly code: StoryImportErrorCode,
    message?: string,
    public readonly cause?: unknown,
  ) {
    super(message ?? code);
    this.name = "StoryImportClientError";
  }
}

type StoryImportFunctionErrorBody = {
  code?: string;
  creditCost?: number | null;
  currentBalance?: number | null;
  estimatedAnalysisTokenCount?: number | null;
  estimatedTokenCount?: number | null;
  estimatedWordCount?: number | null;
  jobId?: string | null;
  message?: string;
  recommendedTier?: SubscriptionTier | null;
  requiredCredits?: number | null;
  tier?: SubscriptionTier | null;
};

function ensureConfigured() {
  if (!isSupabaseConfigured()) {
    throw new StoryImportClientError(
      "missing-config",
      "Supabase environment variables are not configured.",
    );
  }
}

function normalizeStoryImportFunctionError(error: unknown) {
  if (!error || typeof error !== "object") {
    return {
      code: "IMPORT_PROCESSING_FAILED",
      message: "IMPORT_PROCESSING_FAILED",
    } satisfies StoryImportFunctionErrorBody;
  }

  if ("context" in error && typeof error.context === "object" && error.context) {
    const context = error.context as { json?: StoryImportFunctionErrorBody };
    if (context.json) {
      return context.json;
    }
  }

  return {
    code: "message" in error ? String(error.message) : "IMPORT_PROCESSING_FAILED",
    message: "message" in error ? String(error.message) : "IMPORT_PROCESSING_FAILED",
  } satisfies StoryImportFunctionErrorBody;
}

function shouldUseLiveImportModel() {
  const { supabaseUrl } = getClientEnv();

  return !(
    supabaseUrl.includes("127.0.0.1")
    || supabaseUrl.includes("localhost")
  );
}

async function getCurrentUserId() {
  ensureConfigured();

  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();

  if (error || !user) {
    throw new StoryImportClientError("access-denied", "No authenticated user available.", error);
  }

  return user.id;
}

function mapStoryImportQuote(input: {
  creditCost: number;
  currentBalance: number;
  estimatedAnalysisTokenCount?: number | null;
  estimatedTokenCount?: number | null;
  estimatedWordCount?: number | null;
  tier: SubscriptionTier;
}): StoryImportCreditQuote {
  const allowed = input.tier === "author" && input.currentBalance >= input.creditCost;

  return {
    allowed,
    creditCost: input.creditCost,
    currentBalance: input.currentBalance,
    estimatedAnalysisTokenCount: input.estimatedAnalysisTokenCount ?? null,
    estimatedTokenCount: input.estimatedTokenCount ?? null,
    estimatedWordCount: input.estimatedWordCount ?? null,
    reason:
      input.tier !== "author"
        ? "TIER_LOCKED"
        : input.currentBalance < input.creditCost
          ? "INSUFFICIENT_CREDITS"
          : null,
    requiredCredits: input.creditCost,
    tier: input.tier,
  };
}

function mapStoryImportFunctionErrorCode(code: string | null | undefined): StoryImportErrorCode {
  switch (code) {
    case "IMPORT_FILE_INVALID":
      return "invalid-file";
    case "IMPORT_FILE_TOO_LARGE":
      return "file-too-large";
    case "IMPORT_FILE_TOO_MANY_PAGES":
      return "file-too-many-pages";
    case "IMPORT_CANCEL_NOT_ALLOWED":
      return "cancel-not-allowed";
    case "IMPORT_ALREADY_RUNNING":
      return "already-running";
    case "IMPORT_RETRY_NOT_ALLOWED":
      return "retry-not-allowed";
    case "IMPORT_REVIEW_ACTION_FAILED":
      return "review-action-failed";
    case "IMPORT_REVIEW_ELEMENT_ALREADY_RESOLVED":
      return "review-element-resolved";
    case "IMPORT_REVIEW_ELEMENT_NOT_FOUND":
      return "review-element-not-found";
    case "IMPORT_REVIEW_NOT_READY":
      return "review-not-ready";
    case "IMPORT_SOURCE_NOT_FOUND":
      return "source-not-found";
    case "IMPORT_TEXT_EXTRACTION_FAILED":
      return "text-extraction-failed";
    case "INSUFFICIENT_CREDITS":
      return "insufficient-credits";
    case "PROJECT_ACCESS_DENIED":
      return "access-denied";
    case "TIER_LOCKED":
      return "tier-locked";
    default:
      return "start-failed";
  }
}

async function createSignedArtifactUrl(path: string) {
  const result = await supabase.storage
    .from("import-uploads")
    .createSignedUrl(path, 60 * 15);

  if (result.error || !result.data?.signedUrl) {
    throw new StoryImportClientError("generic", result.error?.message, result.error);
  }

  return result.data.signedUrl;
}

async function fetchSignedArtifact<T>(path: string) {
  const signedUrl = await createSignedArtifactUrl(path);
  const response = await fetch(signedUrl);

  if (!response.ok) {
    throw new StoryImportClientError("generic", "The import artifact could not be loaded.");
  }

  return await response.json() as T;
}

export function getStoryImportClientConfig() {
  const env = getClientEnv();
  const importPricing = getRuntimeServicePricing(
    "story_import",
    getRuntimePricingCatalogSnapshot(),
  );

  return {
    creditCost: importPricing.minimumCreditCost,
    fileSizeLimitBytes: env.importFileSizeLimitBytes,
    importPricing,
    maxPageCount: env.importMaxPageCount,
    progressPollMs: env.storyImportProgressPollMs,
  };
}

export async function getStoryImportQuote(
  draft?: Pick<
    StoryImportDraft,
    "fileName" | "fileSizeBytes" | "mimeType" | "projectId" | "storagePath"
  > | null,
): Promise<StoryImportCreditQuote> {
  ensureConfigured();
  const { importPricing } = getStoryImportClientConfig();
  const useLiveModel = shouldUseLiveImportModel();

  if (draft?.storagePath) {
    const result = await supabase.functions.invoke("process-import-v2", {
      body: {
        fileName: draft.fileName,
        filePath: draft.storagePath,
        fileSizeBytes: draft.fileSizeBytes,
        mimeType: draft.mimeType,
        projectId: draft.projectId,
        useLiveModel,
      },
      method: "PUT",
    });

    if (result.error) {
      const normalizedError = normalizeStoryImportFunctionError(result.error);
      throw new StoryImportClientError(
        mapStoryImportFunctionErrorCode(normalizedError.code),
        normalizedError.message,
        result.error,
      );
    }

    const data = (result.data ?? {}) as Partial<StoryImportFunctionErrorBody> & {
      allowed?: boolean;
    };

    return {
      allowed: data.allowed === true,
      creditCost: Number(data.creditCost ?? importPricing.minimumCreditCost),
      currentBalance: Number(data.currentBalance ?? 0),
      estimatedAnalysisTokenCount:
        typeof data.estimatedAnalysisTokenCount === "number"
          ? data.estimatedAnalysisTokenCount
          : null,
      estimatedTokenCount:
        typeof data.estimatedTokenCount === "number" ? data.estimatedTokenCount : null,
      estimatedWordCount:
        typeof data.estimatedWordCount === "number" ? data.estimatedWordCount : null,
      reason:
        data.allowed === true
          ? null
          : data.code === "TIER_LOCKED"
            ? "TIER_LOCKED"
            : data.code === "INSUFFICIENT_CREDITS"
              ? "INSUFFICIENT_CREDITS"
              : null,
      requiredCredits: Number(
        data.requiredCredits ?? data.creditCost ?? importPricing.minimumCreditCost,
      ),
      tier: (data.tier ?? "free") as SubscriptionTier,
    };
  }

  const [subscriptionResult, creditsResult] = await Promise.all([
    supabase.from("subscriptions").select("tier").maybeSingle(),
    supabase.from("credits").select("balance").maybeSingle(),
  ]);

  if (subscriptionResult.error) {
    throw new StoryImportClientError("generic", subscriptionResult.error.message, subscriptionResult.error);
  }

  if (creditsResult.error) {
    throw new StoryImportClientError("generic", creditsResult.error.message, creditsResult.error);
  }

  return mapStoryImportQuote({
    creditCost: calculateStoryImportCreditCost(null, importPricing),
    currentBalance: creditsResult.data?.balance ?? 0,
    estimatedAnalysisTokenCount: null,
    estimatedWordCount: null,
    tier: (subscriptionResult.data?.tier ?? "free") as SubscriptionTier,
  });
}

export async function uploadStoryImportDraft(
  draft: StoryImportDraft,
) {
  const userId = await getCurrentUserId();
  const storagePath = buildStoryImportStoragePath(userId, draft.sessionId, draft.fileName);
  const { error } = await supabase.storage.from("import-uploads").upload(storagePath, draft.file, {
    contentType: draft.mimeType,
    upsert: true,
  });

  if (error) {
    throw new StoryImportClientError("upload-failed", error.message, error);
  }

  return {
    ...draft,
    storagePath,
  };
}

export async function deleteStoryImportDraft(storagePath: string) {
  const { error } = await supabase.storage.from("import-uploads").remove([storagePath]);

  if (error) {
    throw new StoryImportClientError("upload-failed", error.message, error);
  }
}

export async function startStoryImportProcessing(
  draft: StoryImportDraft,
) {
  if (!draft.storagePath) {
    throw new StoryImportClientError(
      "start-failed",
      "Story import processing cannot start without an uploaded source file.",
    );
  }

  const useLiveModel = shouldUseLiveImportModel();
  const { data, error } = await supabase.functions.invoke("process-import-v2", {
    body: {
      fileName: draft.fileName,
      filePath: draft.storagePath,
      fileSizeBytes: draft.fileSizeBytes,
      mimeType: draft.mimeType,
      projectId: draft.projectId,
      useLiveModel,
    },
  });

  if (error || !data) {
    const normalizedError = normalizeStoryImportFunctionError(error);
    throw new StoryImportClientError(
      mapStoryImportFunctionErrorCode(normalizedError.code),
      normalizedError.message,
      error,
    );
  }

  return mapImportJobRecord({
    credits_used: 0,
    current_pass: typeof data.currentPass === "number" ? data.currentPass : 0,
    current_stage_key: typeof data.stageKey === "string" ? data.stageKey : null,
    detected_language: null,
    file_path: draft.storagePath,
    id: String(data.jobId ?? ""),
    pass_label: typeof data.passLabel === "string" ? data.passLabel : null,
    payload_path: null,
    project_id: draft.projectId,
    retry_count: 0,
    source_file_name: draft.fileName,
    status: String(data.status ?? "processing"),
    summary_path: null,
    total_passes: typeof data.totalPasses === "number" ? data.totalPasses : 12,
  });
}

export async function getStoryImportJob(jobId: string) {
  const { data, error } = await supabase
    .from("import_jobs")
    .select("*")
    .eq("id", jobId)
    .maybeSingle();

  if (error || !data) {
    throw new StoryImportClientError("generic", error?.message ?? "Import job not found.", error);
  }

  return mapImportJobRecord(data);
}

export async function getLatestStoryImportJob(options?: {
  includeTerminal?: boolean;
  projectId?: string | null;
}) {
  const statuses: Array<
    | "queued"
    | "pending"
    | "processing"
    | "canceling"
    | "review_ready"
    | "failed"
    | "canceled"
  > =
    options?.includeTerminal
      ? ["queued", "pending", "processing", "canceling", "review_ready", "failed", "canceled"]
      : ["queued", "pending", "processing", "canceling"];

  let query = supabase
    .from("import_jobs")
    .select("*")
    .in("status", statuses)
    .or("error_text.is.null,error_text.neq.IMPORT_DISCARDED")
    .order("created_at", { ascending: false })
    .limit(1);

  if (options?.projectId) {
    query = query.eq("project_id", options.projectId);
  }

  const { data, error } = await query.maybeSingle();

  if (error) {
    throw new StoryImportClientError("generic", error.message, error);
  }

  return data ? mapImportJobRecord(data) : null;
}

async function getImportJobArtifacts(jobId: string) {
  const { data, error } = await supabase
    .from("import_jobs")
    .select(
      "completed_at, detected_format, id, payload_path, project_id, source_file_name, status, summary_path",
    )
    .eq("id", jobId)
    .maybeSingle();

  if (error || !data) {
    throw new StoryImportClientError("generic", error?.message ?? "Import job not found.", error);
  }

  return data;
}

export async function readStoryImportSummary(jobId: string): Promise<StoryImportSummary> {
  const data = await getImportJobArtifacts(jobId);

  if (!data.summary_path) {
    throw new StoryImportClientError(
      "review-not-ready",
      "The import summary is not ready yet.",
    );
  }

  const summary = await fetchSignedArtifact<Record<string, unknown>>(data.summary_path);
  return mapStoryImportSummaryArtifact({
    ...summary,
    completedAt: typeof summary.completedAt === "string" ? summary.completedAt : data.completed_at,
    detectedFormat:
      typeof summary.detectedFormat === "string" ? summary.detectedFormat : data.detected_format,
    jobId: typeof summary.jobId === "string" ? summary.jobId : data.id,
    projectId:
      typeof summary.projectId === "string" ? summary.projectId : data.project_id,
    sourceFileName:
      typeof summary.sourceFileName === "string"
        ? summary.sourceFileName
        : data.source_file_name,
  });
}

export async function readStoryImportReviewBundle(
  jobId: string,
): Promise<StoryImportReviewBundle> {
  const data = await getImportJobArtifacts(jobId);

  if (!data.payload_path) {
    throw new StoryImportClientError(
      "review-not-ready",
      "The staged review bundle is not ready yet.",
    );
  }

  const bundle = await fetchSignedArtifact<Record<string, unknown>>(data.payload_path);
  const mappedBundle = mapStoryImportReviewBundle(bundle);

  if (!mappedBundle) {
    throw new StoryImportClientError(
      "review-not-ready",
      "The staged review bundle is unavailable.",
    );
  }

  return mappedBundle;
}

export async function retryStoryImportJob(jobId: string) {
  const useLiveModel = shouldUseLiveImportModel();
  const { error } = await supabase.functions.invoke("process-import-v2", {
    body: {
      action: "retry_job",
      jobId,
      useLiveModel,
    },
    method: "PATCH",
  });

  if (error) {
    const normalizedError = normalizeStoryImportFunctionError(error);
    throw new StoryImportClientError(
      mapStoryImportFunctionErrorCode(normalizedError.code),
      normalizedError.message,
      error,
    );
  }

  return getStoryImportJob(jobId);
}

export async function cancelStoryImportJob(jobId: string) {
  const { error } = await supabase.functions.invoke("process-import-v2", {
    body: {
      action: "cancel_job",
      jobId,
    },
    method: "PATCH",
  });

  if (error) {
    const normalizedError = normalizeStoryImportFunctionError(error);
    throw new StoryImportClientError(
      mapStoryImportFunctionErrorCode(normalizedError.code),
      normalizedError.message,
      error,
    );
  }

  return getStoryImportJob(jobId);
}

export async function continueStoryImportJob(jobId: string) {
  const { error } = await supabase.functions.invoke("process-import-v2", {
    body: {
      action: "continue_job",
      jobId,
    },
    method: "PATCH",
  });

  if (error) {
    const normalizedError = normalizeStoryImportFunctionError(error);
    throw new StoryImportClientError(
      mapStoryImportFunctionErrorCode(normalizedError.code),
      normalizedError.message,
      error,
    );
  }

  return getStoryImportJob(jobId);
}

export async function discardStoryImportJob(jobId: string) {
  const { error } = await supabase.functions.invoke("process-import-v2", {
    body: {
      action: "discard_job",
      jobId,
    },
    method: "PATCH",
  });

  if (error) {
    const normalizedError = normalizeStoryImportFunctionError(error);
    throw new StoryImportClientError(
      mapStoryImportFunctionErrorCode(normalizedError.code),
      normalizedError.message,
      error,
    );
  }
}

export async function acceptStoryImportElement(input: {
  editedElementData?: Record<string, unknown>;
  elementId: string;
  jobId: string;
}) {
  const { data, error } = await supabase.functions.invoke("process-import-v2", {
    body: {
      action: "accept_element",
      editedElementData: input.editedElementData,
      elementId: input.elementId,
      jobId: input.jobId,
    },
    method: "PATCH",
  });

  if (error || !data) {
    const normalizedError = normalizeStoryImportFunctionError(error);
    throw new StoryImportClientError(
      mapStoryImportFunctionErrorCode(normalizedError.code),
      normalizedError.message,
      error,
    );
  }

  return data as StoryImportReviewActionResult;
}

export async function discardStoryImportElement(input: {
  elementId: string;
  jobId: string;
}) {
  const { data, error } = await supabase.functions.invoke("process-import-v2", {
    body: {
      action: "discard_element",
      elementId: input.elementId,
      jobId: input.jobId,
    },
    method: "PATCH",
  });

  if (error || !data) {
    const normalizedError = normalizeStoryImportFunctionError(error);
    throw new StoryImportClientError(
      mapStoryImportFunctionErrorCode(normalizedError.code),
      normalizedError.message,
      error,
    );
  }

  return data as StoryImportReviewActionResult;
}

export async function bulkAcceptStoryImportElementType(input: {
  elementType: StoryImportBulkAcceptElementType;
  jobId: string;
}) {
  const { data, error } = await supabase.functions.invoke("process-import-v2", {
    body: {
      action: "bulk_accept_type",
      elementType: input.elementType,
      jobId: input.jobId,
    },
    method: "PATCH",
  });

  if (error || !data) {
    const normalizedError = normalizeStoryImportFunctionError(error);
    throw new StoryImportClientError(
      mapStoryImportFunctionErrorCode(normalizedError.code),
      normalizedError.message,
      error,
    );
  }

  return data as StoryImportReviewActionResult;
}

export function subscribeToStoryImportJobs(
  userId: string,
  onUpdate: (jobId: string) => void,
) {
  const channel = supabase
    .channel(`story-import-${userId}`)
    .on(
      "postgres_changes",
      {
        event: "UPDATE",
        schema: "public",
        table: "import_jobs",
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        const jobId = typeof payload.new.id === "string" ? payload.new.id : null;
        if (jobId) {
          onUpdate(jobId);
        }
      },
    )
    .subscribe();

  return () => {
    void supabase.removeChannel(channel);
  };
}

export async function getCurrentStoryImportUserId() {
  return getCurrentUserId();
}
